import html2canvas from "html2canvas";
import jsPdf from "jspdf";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PhotoGallery } from "@/components/PhotoGallery";
import { QuotationView } from "@/components/QuotationView";
import { type ScopeOfWorkData, ScopeOfWorkView } from "@/components/quote/ScopeOfWorkView";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import type { QuoteStatus } from "./StatusBadge";

interface Quote {
  id: string;
  project_id: string;
  quote_name: string;
  quote_number: string;
  status: QuoteStatus;
  notes?: string;
  total_amount: number;
  scheduled_date?: string | null;
  created_at: string;
  updated_at: string;
}

interface QuoteItem {
  id: string;
  quote_id: string;
  project_item_id?: string;
  component_type: string;
  product_name: string;
  product_description?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes?: string;
  photos?: string[];
}

interface QuoteService {
  id: string;
  quote_id: string;
  project_service_id?: string;
  service_id: string;
  status: string;
}

interface Project {
  id: string;
  customer_name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  budget?: string;
  timeframe?: string;
  description?: string;
  created_at: string;
}

interface QuoteViewerProps {
  quote: Quote | null;
  project: Project | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuoteViewer({ quote, project, open, onOpenChange }: QuoteViewerProps) {
  const [quoteItems, setQuoteItems] = useState<QuoteItem[]>([]);
  const [quoteServices, setQuoteServices] = useState<QuoteService[]>([]);
  const [photos, setPhotos] = useState<
    Array<{
      url: string;
      serviceName: string;
      instanceName: string;
      fieldName: string;
    }>
  >([]);
  const [quoteSummary, setQuoteSummary] = useState<ScopeOfWorkData | null>(null);
  const [loading, setLoading] = useState(false);
  const quotationRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const loadQuoteData = useCallback(async () => {
    if (!quote) {
      return;
    }

    try {
      setLoading(true);

      const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

      // Load photos from API
      const photosResponse = await fetch(`${apiUrl}/api/v1/checklist-quotes/${quote.id}/photos`);
      if (photosResponse.ok) {
        const photosData = await photosResponse.json();
        setPhotos(photosData.data || []);
      }

      // Load quote summary for checklist-based quotes
      const summaryResponse = await fetch(`${apiUrl}/api/v1/checklist-quotes/${quote.id}/summary`);
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setQuoteSummary(summaryData.data || null);
      }

      // For checklist-based quotes, there are no traditional "items"
      // The quote data comes from checklist responses which are rendered differently
      setQuoteItems([]);
      setQuoteServices([]);
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to load quote details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [quote, toast]);

  useEffect(() => {
    if (!(quote && open)) {
      return;
    }
    loadQuoteData().catch(() => {
      // Errors are surfaced via toast inside loadQuoteData.
    });
  }, [quote, open, loadQuoteData]);

  // Print functionality
  const handlePrint = () => {
    if (!quotationRef.current) return;

    // Use native browser print
    window.print();
  };

  // PDF download functionality
  const handleDownloadPdf = async () => {
    if (!(quotationRef.current && quote)) {
      return;
    }

    try {
      toast({
        title: "Generating PDF",
        description: "Please wait while we generate your PDF...",
      });

      const canvas = await html2canvas(quotationRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPdf({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight) * 0.95;
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf.addImage(imgData, "PNG", imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`${quote.quote_number}-${project?.customer_name}.pdf`);

      toast({
        title: "PDF Downloaded",
        description: "Your quotation has been downloaded successfully.",
      });
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Transform quote data to match QuotationView expectations
  const quotationData = useMemo(() => {
    if (!(quote && project)) {
      return null;
    }

    // Map quote services to service names for display
    const mappedServices = quoteServices.map((qs) => qs.service_id);

    return {
      project: {
        id: project.id,
        customer_name: project.customer_name,
        email: project.email,
        phone: project.phone,
        address: project.address,
        city: project.city,
        budget: project.budget,
        timeframe: project.timeframe,
        description: project.description,
        services: mappedServices,
        created_at: project.created_at,
      },
      items: quoteItems.map((item) => ({
        id: item.id,
        component_type: item.component_type,
        product_name: item.product_name,
        product_description: item.product_description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price,
        notes: item.notes,
        photos: item.photos,
      })),
      quoteNumber: quote.quote_number,
      quoteName: quote.quote_name,
      quoteNotes: quote.notes,
    };
  }, [project, quote, quoteServices, quoteItems]);

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading quote details...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!quotationData) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="print:hidden">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <span>
                {quote?.quote_name} - {quote?.quote_number}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription>
            Review and generate this quotation using your existing quotation system.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Photo Gallery */}
          {photos.length > 0 && (
            <div className="print:hidden">
              <PhotoGallery photos={photos} />
            </div>
          )}

          {/* Render based on quote type */}
          {quoteSummary ? (
            /* Checklist-based quote - use ScopeOfWorkView */
            <ScopeOfWorkView
              ref={quotationRef}
              data={{
                ...quoteSummary,
                project: project || undefined,
              }}
              showPrintHeader={true}
            />
          ) : (
            /* Traditional quote - use QuotationView */
            <QuotationView
              ref={quotationRef}
              project={quotationData.project}
              items={quotationData.items}
              quoteNumber={quotationData.quoteNumber}
              quoteName={quotationData.quoteName}
              quoteNotes={quotationData.quoteNotes}
              onBack={() => onOpenChange(false)}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
